import { AfterViewInit, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LoaderService } from 'modules/app/services/loader.service';
import translationsEN from '../../../../assets/i18n/en';
import translationsES from '../../../../assets/i18n/es';
import { HttpClient } from '@angular/common/http';
import { ApiGatewayHelperService } from 'modules/app/services/apigateway-helper.service';
import { StudentBookService } from 'modules/foc/services/student-book.service';
import { AppHelperService } from 'modules/app/services/app-helper.service';

@Component({
  selector: 'app-media-player',
  templateUrl: './media-player.component.html',
  styleUrls: ['./media-player.component.scss']
})
export class MediaPlayerComponent implements OnInit, AfterViewInit {

  translations: any = {}; // Use 'any' if translations are nested objects
  productId?: string;
  mediaId?: string;
  spaceId?: string;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly loaderService: LoaderService,
    private readonly http: HttpClient,
    private readonly apiGatewayHelperService: ApiGatewayHelperService,
    private readonly studentBookService: StudentBookService,
    private readonly appHelperService: AppHelperService
  ) { }
  ngAfterViewInit(): void {
    this.loaderService.hideLoader();
    this.fetchBundle();
  }

  ngOnInit(): void {
    console.log('MediaPlayerComponent initialized');
    this.setTranslations();
    this.productId = this.route.snapshot.params['productCode'];
    this.spaceId = this.route.snapshot.params['spaceId'];
    this.mediaId = this.route.snapshot.url.map(e => e.path)[this.route.snapshot.url.length-1];
    console.log("🚀 ~ MediaPlayerComponent ~ ngOnInit ~ this.spaceId :", this.spaceId )
  }

  private setTranslations(): void {
    this.translations = document.documentElement.lang === 'es' ? translationsES : translationsEN;
  }

  async fetchBundle(): Promise<void> {
    try {
      if (!this.productId || this.mediaId === undefined) {
        throw new Error('Missing product or media ID');
      }
      const product = await this.http.get<Product>(
        this.apiGatewayHelperService.getApiGatewayPrefixUrl() + `products/${this.productId}`
      ).toPromise();

      if (!product?.assets?.items?.default) {
        throw new Error('Product assets not found');
      }

      const data = await this.http.get<any>(
        this.apiGatewayHelperService.getApiGatewayPrefixUrl() + `${this.spaceId}/entitlements`
      ).toPromise();

      for (const bundle of Object.keys(product.bundles)) {
        const bundleProducts = product.bundles[bundle].products;
        for (const bundleProduct of bundleProducts) {
          if(product.productcode == bundleProduct['dls-product'].code) {
            const isActive = this.studentBookService.isProductActive(bundleProduct, data?.userEntitlements, data?.schoolEntitlements);
            if (!isActive) {
              throw new Error('Product is not active');
            }
          }
        }
      }

      if(product.meta.visibility && product.meta.visibility != 'true') {
        throw new Error('Product is not visible');
      }

      const mediaAsset = this.getMediaAsset(product.assets.items.default, this.mediaId)

      if (!mediaAsset) {
        throw new Error('Media asset not found');
      }

      const assetType = mediaAsset['sub-type'];
      this.launchMediaPlayer(product, mediaAsset, assetType);
    } catch (error) {
      console.error('error:', error);
      this.appHelperService.navigateToDashboardApp([]);
    } finally {
      this.loaderService.hideLoader();
    }
  }

  launchMediaPlayer(product: Product, mediaAsset: Asset, assetType: AssetType): void {
    const mediaUrls = this.getMediaUrls(product, mediaAsset, assetType);
    const options: MediaOptions = {
      basepath: mediaUrls.basepath,
      jsonData: {
        type: assetType,
        [assetType]: {
          path: mediaUrls.mediaUrl,
          subtitles: mediaUrls.captionsUrl
        },
      },
      onClose: () => {
        window.close();
      },
      translations: this.translations,
      events: {
        onOpen: undefined,
        onDragStart: undefined,
        navigateToDashboard: undefined
      },
      isIosDevice: this.isIosDevice,
    };
    if (assetType === 'video-package' || assetType === 'video') {
      const player = window['dlsVideoPlayer'];
      player?.launchPlayer(options);
    } else if (assetType === 'audio-package' || assetType === 'audio') {
      const player = window['dlsAudioPlayer'];
      player?.launchPlayer(options);
    } else {
      console.error('Unsupported asset type:', assetType);
    }
  }

  private getMediaUrls(product: Product, mediaAsset: Asset, assetType: AssetType): { basepath: string, mediaUrl: string, captionsUrl: string } {
    const basepath = product.meta.paths['public-assets'];
    const baseMediaUrl = `${mediaAsset[assetType].url}`;
    if (assetType === 'video-package') {
      const mediaPackageFilename = encodeURIComponent(mediaAsset[assetType].filename);
      return {
        basepath,
        mediaUrl: `${baseMediaUrl}/${mediaPackageFilename}`.replace('.zip', '.mp4'),
        captionsUrl: `${baseMediaUrl}/${mediaPackageFilename}`.replace('.zip', '.vtt')
      };
    } else {
      return {
        basepath,
        mediaUrl: `${baseMediaUrl}`,
        captionsUrl: `${baseMediaUrl}`
      };
    }
  }

  private isIosDevice(): boolean {
    const platform = (navigator as any).userAgentData?.platform;
    if (platform) {
      return platform === 'iOS';
    }
    // Fallback for older browsers
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const hasMSStream = 'MSStream' in window;
    return isIOS && !hasMSStream;
  }

  private getMediaAsset(items, val) {
    if (!Array.isArray(items)) return undefined;
    for (const item of items) {
        if (item['item-type'] === 'folder' && Array.isArray(item.items)) {
            const found = this.getMediaAsset(item.items, val);
            if (found) return found;
        }
        const code = item['item-code'];
        const lastSegment = code.substring(code.lastIndexOf('/') + 1);
        if (lastSegment === val) {
            return item;
        }
    }
    return undefined;
  }

}

interface Product {
  readonly assets: {
    readonly items: {
      readonly default: Asset[];
    };
  };
  readonly meta: {
    readonly paths: {
      readonly "public-assets": string;
    };
    readonly visibility: 'true' | 'false' | 'coming-soon';
  };
  readonly bundles: {
    readonly [key: string]: any;
  }[];
  readonly productcode: string;
}

interface Asset {
  readonly "item-code": string;
  readonly "sub-type": AssetType;
  readonly "audio-package": { readonly url: string, readonly filename: string };
  readonly "video-package": { readonly url: string, readonly filename: string };
  readonly "audio": { readonly url: string };
  readonly "video": { readonly url: string };
}

type AssetType = 'audio' | 'video' | 'audio-package' | 'video-package';

interface JsonData {
  readonly type: AssetType;
  readonly 'audio-package'?: {
    readonly path: string;
    readonly subtitles: string;
  };
  readonly 'video-package'?: {
    readonly path: string;
    readonly subtitles: string;
  };
  readonly 'audio'?: {
    readonly path: string;
    readonly subtitles: string;
  };
  readonly 'video'?: {
    readonly path: string;
    readonly subtitles: string;
  };
}

interface MediaOptions {
  readonly jsonData: JsonData;
  readonly basepath: string;
  readonly onClose: () => void;
  readonly translations: Record<string, string>;
  readonly events: {
    readonly onOpen?: () => void;
    readonly onDragStart?: () => void;
    readonly navigateToDashboard?: () => void;
  };
  readonly isIosDevice: () => boolean;
}
